import AddIcon from '@mui/icons-material/Add';
import DnsIcon from '@mui/icons-material/Dns';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { Alert, Box, Button, Typography } from '@mui/material';
import { InstanceDetailModal } from '../components/modal/InstanceDetailModal';
import { DeleteInstanceModal } from '../components/modal/DeleteInstanceModal';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TablePaginationBar } from '../../../components/ui/TablePaginationBar';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import { useTableState } from '../../../hooks/useTableState';
import theme from '../../../theme/theme';
import { NewInstanceModal } from '../components/modal/NewInstanceModal';
import { useGenerateKey } from '../hooks/useGenerateKey';
import { useGetInstances } from '../hooks/useGetInstances';
import { useGetTerrains } from '../hooks/useGetTerrains';
import { useInstanceColumns } from '../hooks/useInstanceColumns';
import { useFetch } from '../../../hooks/useFetch';
import { InstanceApiFactory } from '../api/instanceApi';
import type { InstanceRow } from '../instanceTypes';
import { usePermissions } from '../../permissions/usePermissions';

export function InstanceListPage() {
  const { t } = useTranslation(['instances', 'common']);
  const {
    page,
    limit,
    setPage,
    setLimit,
    filters,
    debouncedFilters,
    setFilter,
    clearFilter,
    clearAll,
    activeFilterKeys,
    sortState,
    handleSortChange,
  } = useTableState<InstanceRow>();

  const [detailInstance, setDetailInstance] = useState<InstanceRow | null>(null);
  const [deleteInstance, setDeleteInstance] = useState<InstanceRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [client] = useFetch();
  const api = useMemo(() => InstanceApiFactory(client), [client]);

  // Actions follow the `instances` line of the catalogue (ADR-040 D4/D5).
  const { can } = usePermissions();
  const canCreate = can('instances', 'create');
  const canModify = can('instances', 'modify');
  const canDelete = can('instances', 'delete');

  const { data: terrains } = useGetTerrains();
  const terrainOptions = useMemo(() => terrains.map(t => t.codeterrain), [terrains]);
  const terrainMap = useMemo(
    () => new Map(terrains.map(t => [t.codeterrain, t])),
    [terrains],
  );

  // refetch is defined below (it needs `columns`, which needs `generateKey`);
  // bridge via a ref so generating a key can refresh the list (key-status column).
  const refetchRef = useRef<() => void>(() => {});
  const refreshList = useCallback(() => refetchRef.current(), []);

  const { generateKey, generatingKeyId } = useGenerateKey({ onSuccess: refreshList });

  const columns = useInstanceColumns({
    terrainOptions,
    terrainMap,
    onEdit: row => setDetailInstance(row),
    onGenerateKey: generateKey,
    onDelete: row => setDeleteInstance(row),
    generatingKeyId,
    canModify,
    canDelete,
  });

  const {
    data: apiData,
    loading,
    error,
    refetch,
    meta,
  } = useGetInstances(debouncedFilters, columns, sortState, page, limit);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  // Server-side pagination: the API returns exactly the current page + its meta.
  const count = meta?.total ?? 0;

  // If the current page no longer exists (e.g. last row of the last page was
  // deleted), clamp back to the last valid page instead of showing an empty page.
  useEffect(() => {
    if (!loading && meta && meta.total > 0 && page > meta.totalPages) {
      setPage(Math.max(1, meta.totalPages));
    }
  }, [loading, meta, page, setPage]);

  const activeFilterCount = activeFilterKeys.size;

  // "No instances" only when the base truly has none — an empty page with
  // total > 0 is a pagination state, not an empty dataset.
  const customEmptyState =
    !loading && !error && count === 0 && apiData.length === 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 7,
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DnsIcon sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.4) }} />
        </Box>
        <Typography variant="body1" fontWeight={theme.typography.fontWeightSemiBold}>
          {t('instances:empty.title')}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {Object.keys(filters).length > 0
            ? t('instances:empty.hintFilter')
            : canCreate
              ? t('instances:empty.hintEmpty')
              : t('instances:empty.hintEmptyReadOnly')}
        </Typography>
        {canCreate && Object.keys(filters).length === 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ borderRadius: 5, textTransform: 'none', mt: 1 }}
            onClick={e => {
              (e.currentTarget as HTMLElement).blur();
              setCreateOpen(true);
            }}
          >
            {t('instances:actions.newInstance')}
          </Button>
        )}
      </Box>
    ) : undefined;

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[50],
        height: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Full-bleed header banner — same design as the employees list/detail
          SubHeader (light-primary background, gradient icon badge + title). */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          background: alpha(theme.palette.primary.main, 0.06),
          color: theme.palette.text.body,
        }}
      >
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <DnsIcon sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }} />
        </Box>

        <Typography
          sx={{
            fontSize: theme.typography.employeeName.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.primary.main,
          }}
        >
          {t('instances:pageTitle')}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {canCreate && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            aria-label={t('instances:actions.newInstance')}
            sx={{ borderRadius: 5, textTransform: 'none' }}
            onClick={e => {
              (e.currentTarget as HTMLElement).blur();
              setCreateOpen(true);
            }}
          >
            {t('instances:actions.newInstance')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 4,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {t('instances:errors.loadError')}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <TableTemplate<InstanceRow>
            columns={columns}
            rows={apiData}
            striped
            stickyHeader
            fillHeight
            loading={loading}
            errorState={customEmptyState}
            toolbarActions={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* No meta yet (initial load, error) means the total is unknown —
                    showing « 0 » would be misleading. */}
                {meta && (
                  <Typography variant="body2" color="text.label">
                    <b>{count}</b> {t('instances:count', { count })}
                  </Typography>
                )}
                {activeFilterCount > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<FilterListOffIcon />}
                    onClick={clearAll}
                    sx={{ borderRadius: 5, textTransform: 'none' }}
                  >
                    {t('common:table.clearFilters')} ({activeFilterCount})
                  </Button>
                )}
              </Box>
            }
            sortState={sortState}
            onSortChange={handleSortChange}
            filterState={filters}
            onFilterChange={setFilter}
            onFilterClear={clearFilter}
            onFilterClearAll={clearAll}
            onRowClick={row => setDetailInstance(row)}
            pagination={
              meta ? (
                <TablePaginationBar
                  meta={meta}
                  page={page}
                  limit={limit}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                />
              ) : undefined
            }
          />
        </Box>

        <NewInstanceModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => refetch()}
        />

        <InstanceDetailModal
          instance={detailInstance}
          open={detailInstance !== null}
          onClose={() => setDetailInstance(null)}
          onSaved={() => refetch()}
          onKeyRevoked={() => refetch()}
          onKeyGenerated={() => refetch()}
          readOnly={!canModify}
        />

        <DeleteInstanceModal
          instance={deleteInstance}
          onClose={() => setDeleteInstance(null)}
          onDelete={api.delete}
          onDeleted={() => {
            setDeleteInstance(null);
            refetch();
          }}
        />
      </Box>
    </Box>
  );
}
